import { stripPointing } from './content'

/**
 * The shortest phrase a search over a book will answer.
 *
 * **Three, because that is what the index can serve.** The match is a substring
 * one, so it runs against a gin/pg_trgm index over the pointing-stripped text,
 * and pg_trgm indexes three-character sequences — a query shorter than that has
 * no trigram to look up and Postgres reads every row in the corpus instead. A
 * surface accepting one character does not fail; it silently sequential-scans,
 * which is worse because it looks like it works.
 *
 * A bound on the LETTERS rather than on what was typed: pointing is removed
 * before counting, so a fully vocalized Hebrew word of three letters qualifies
 * even though its string is more than twice that long.
 */
export const MIN_QUERY_LETTERS = 3

/**
 * How the words are normalized before they are compared.
 *
 * **This is the contract every search over a book meets**, and it is stated
 * here rather than beside any one of them because more than one thing has to
 * agree with it: the query that finds passages, the surface that decides
 * whether a phrase is long enough to send, and a replace that has to offer
 * exactly the passages the search returned. Two of those match over different
 * material — a flattened stored column and the content runs a passage is made
 * of — and offering a different set from either is the same bug whichever way
 * round it happens.
 *
 * Three rules, and nothing else:
 *
 * 1. **Pointing is removed, from both sides.** A reader types letters, and the
 *    stored text carries a mark between every one of them.
 * 2. **Case is ignored**, which matters only for the Latin in a book — Hebrew
 *    has no case to fold.
 * 3. **Whitespace is left as it stands.** The flattener joins a passage's parts
 *    with a newline — a verse's reference, its Hebrew, its translation, its
 *    classification — so a phrase running from the end of one part into the
 *    start of the next does not match, and should not: those are two lines on
 *    the page rather than one sentence.
 *
 * What is NOT applied, so that nothing has to guess: no stemming, no folding of
 * final letters, no transliteration, and no ignoring of maqaf or the other
 * punctuation sitting inside the Hebrew range. A match is the letters, as they
 * stand.
 */
export const normalizedFor = (query: string) => stripPointing(query).trim()

/** Whether this is enough of a phrase to look up at all. */
export const longEnoughToSearch = (query: string) =>
  normalizedFor(query).length >= MIN_QUERY_LETTERS

/**
 * Case, folded WITHOUT changing how long the string is.
 *
 * Length-preserving because a match is mapped back to the coordinates of the
 * text it was found in: an output longer than its input would shift every
 * position after it, and the change would land on the wrong words. Where a
 * character has no lower case of the same length it is left as it was, so it
 * simply does not fold — a character that fails to match is a far smaller fault
 * than a range that lands in the wrong place. `İ` is the character that proves
 * it: `toLowerCase` returns two code points for it, so folding the haystack and
 * then indexing into the original takes `osef` out of `İstanbul Yosef` and
 * leaves the `Y` standing.
 *
 * Stated here beside the rest of the contract rather than beside either of its
 * callers, because the search that finds a phrase and the replace that rewrites
 * it must fold identically or they disagree about what they found.
 */
export const foldCase = (s: string): string => {
  let out = ''
  for (const ch of s) {
    const lower = ch.toLowerCase()
    out += lower.length === ch.length ? lower : ch
  }
  return out
}
